import { ref } from "vue";
import { defineStore } from "pinia";
import { useToast } from "vue-toastification";
import apiClient from "@/axios";
import { setDefaultTablePageSize } from "@/utils/tablePagination";

export const defaultESFSettings = {
  id: 0,
  defaultInvoiceSubject: null,
  defaultInvoicePattern: null,
  defaultSettlementType: null,
  buyerId: null,
  defaultTheme: null,
  agreement: true,
  defaultPagingCount: null,
  defaultPage: null,
  taxRate: null,
  activeCalculate: true,
  consfeePercentage: 0,
  sproPercentage: 0,
  brosPercentage: 0,
  aiActive: true,
  isMaster: null,
  invoiceFormType: null,
  defaultCompanyCurrency: null,
  actionColumnPosition: "right",
  invoiceEntryMode: "stepped",
  autoSendInvoiceSmsToBuyer: false,
  autoSendInvoiceSmsToSystemManager: false,
  sendLoginSms: false,
  smsUsername: "",
  smsPassword: "",
  smsSenderNumber: "",
  systemManagerSmsText: "فاکتور شماره <factnumber> برای <sherkatname> ثبت و به سامانه مودیان ارسال گردید.",
  buyerSmsText: "<buyername> عزیز\nصورتحساب شماره <factnumber>  از طرف <sherkatname> در سامانه مودیان ثبت گردید.",
  companyId: 0
};

export const useESFSettingsStore = defineStore("esfSettings", () => {
  const settings = ref({ ...defaultESFSettings });
  const loading = ref(false);
  const loaded = ref(false);
  let fetchPromise = null;
  const toast = useToast();

  function normalizeSettings(data) {
    const result = data?.result ?? data?.data ?? data ?? {};
    const defaultPage =
      result.defaultPage ??
      result.DefaultPage ??
      result.default_page ??
      defaultESFSettings.defaultPage;
    const actionColumnPosition =
      result.actionColumnPosition ??
      result.ActionColumnPosition ??
      result.action_column_position ??
      defaultESFSettings.actionColumnPosition;
    const defaultPagingCount =
      result.defaultPagingCount ??
      result.DefaultPagingCount ??
      result.default_paging_count ??
      defaultESFSettings.defaultPagingCount;

    setDefaultTablePageSize(defaultPagingCount);
    settings.value = { ...defaultESFSettings, ...result, defaultPage, actionColumnPosition, defaultPagingCount };
    loaded.value = true;
    return settings.value;
  }

  async function fetchSettings(options = {}) {
    const silent = Boolean(options?.silent);
    if (fetchPromise) return fetchPromise;
    loading.value = true;

    fetchPromise = (async () => {
      try {
        const response = await apiClient.get("/ESFSetting/GetCompanySetting");
        return normalizeSettings(response.data);
      } catch (error) {
        console.error("Error fetching ESF settings:", error);
        if (!silent) toast.error("خطا در دریافت تنظیمات مودیان");
        return settings.value;
      } finally {
        loading.value = false;
        fetchPromise = null;
      }
    })();

    return fetchPromise;
  }

  async function saveSettings(payload) {
    try {
      if (Number(payload.id)) {
        await updateSetting(payload);
      } else {
        await addSetting(payload);
      }

      return await fetchSettings();
    } catch (error) {
      console.error("Error saving ESF settings:", error);
    }
  }

  async function addSetting(payload) {
    loading.value = true;

    try {
      const response = await apiClient.post("/ESFSetting", payload);
      normalizeSettings(response.data?.result ?? response.data ?? payload);
      toast.success("تنظیمات مودیان با موفقیت ذخیره شد");
      return settings.value;
    } catch (error) {
      console.error("Error saving global information:", error);
      toast.error("خطا در ثبت تنظیمات مودیان");
    } finally {
      loading.value = false;
    }
  }

  async function updateSetting(payload) {
    loading.value = true;

    try {
      const response = await apiClient.put("/ESFSetting", payload);
      normalizeSettings(response.data?.result ?? response.data ?? payload);
      toast.success("تنظیمات مودیان با موفقیت ذخیره شد");
      return settings.value;
    } catch (error) {
      console.error("Error updating global information:", error);
      toast.error("خطا در ویرایش تنظیمات مودیان");
    } finally {
      loading.value = false;
    }
  }

  return {
    settings,
    loading,
    loaded,
    fetchSettings,
    saveSettings
  };
});
